import React, {useEffect, useState} from "react";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import Button from "../../../../../components/Button";
import {SaveIcon} from "lucide-react";
import {
    AcceptanceMode,
    CompetitorType,
    EntryFeeMode,
    RegistrationConfig,
    RosterDetailLevel,
    TeamNamingMode,
} from "../../../../../types/tournament";
import {useCategoryRegistration, useUpdateCategoryRegistration} from "../../../../../hooks/useTournaments";

function isoToLocalInput(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string | null {
    const s = (local || "").trim();
    if (!s) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

const ROSTER_LABELS: Record<RosterDetailLevel, string> = {
    NAME_ONLY: "Name only — just the competitor / team name",
    NAMED_MEMBERS: "Named members — collect each roster member's name",
};

const TEAM_NAMING_LABELS: Record<TeamNamingMode, string> = {
    TEAM_NAME: "Team name — collect a team name on registration",
    PLAYER_FIRST_NAMES: "Player first names — e.g. \"John/Jan\"",
    PLAYER_LAST_NAMES: "Player last names — e.g. \"Doe/Man\"",
    PLAYER_FULL_NAMES: "Player full names — e.g. \"John Doe/Jan Man\"",
};

// Form mirrors RegistrationConfig but keeps numeric fields as strings for inputs.
type RegForm = {
    entryFeeMode: EntryFeeMode;
    entryFeeAmount: string;
    acceptanceMode: AcceptanceMode;
    capacity: string;
    waitlistEnabled: boolean;
    pendingConsumesCapacity: boolean;
    rosterDetailLevel: RosterDetailLevel;
    teamNamingMode: TeamNamingMode;
    registrationOpenAt: string;
    registrationCloseAt: string;
    businessCoversTransactionFee: boolean;
};

const EMPTY_FORM: RegForm = {
    entryFeeMode: "FREE",
    entryFeeAmount: "",
    acceptanceMode: "AUTO_ACCEPT",
    capacity: "",
    waitlistEnabled: false,
    pendingConsumesCapacity: false,
    rosterDetailLevel: "NAME_ONLY",
    teamNamingMode: "TEAM_NAME",
    registrationOpenAt: "",
    registrationCloseAt: "",
    businessCoversTransactionFee: false,
};

type Props = {
    businessId: string;
    tournamentId: string;
    categoryId: string;
    // Competitor type comes from the play category; it controls the roster-detail section.
    competitorType: CompetitorType;
    currency?: string | null;
};

export default function CategoryRegistrationForm({
                                                     businessId,
                                                     tournamentId,
                                                     categoryId,
                                                     competitorType,
                                                     currency,
                                                 }: Props) {
    const {data: loaded, isLoading} = useCategoryRegistration(businessId, tournamentId, categoryId);
    const updateMutation = useUpdateCategoryRegistration(businessId, tournamentId, categoryId);

    const [form, setForm] = useState<RegForm>(EMPTY_FORM);

    const isTeam = competitorType === "TEAM";

    useEffect(() => {
        const r = loaded;
        if (!r) return;
        setForm({
            entryFeeMode: r.entryFeeMode ?? "FREE",
            entryFeeAmount: r.entryFeeAmount != null ? String(r.entryFeeAmount) : "",
            acceptanceMode: r.acceptanceMode ?? "AUTO_ACCEPT",
            capacity: r.capacity != null ? String(r.capacity) : "",
            waitlistEnabled: Boolean(r.waitlistEnabled),
            pendingConsumesCapacity: Boolean(r.pendingConsumesCapacity),
            rosterDetailLevel: r.rosterDetailLevel ?? "NAME_ONLY",
            teamNamingMode: r.teamNamingMode ?? "TEAM_NAME",
            registrationOpenAt: isoToLocalInput(r.registrationOpenAt),
            registrationCloseAt: isoToLocalInput(r.registrationCloseAt),
            businessCoversTransactionFee: Boolean(r.businessCoversTransactionFee),
        });
    }, [loaded]);

    const dateError =
        form.registrationOpenAt && form.registrationCloseAt &&
        new Date(form.registrationCloseAt).getTime() <= new Date(form.registrationOpenAt).getTime()
            ? "Registration close must be after registration open."
            : null;

    const save = async () => {
        if (dateError) {
            notify.error(dateError);
            return;
        }
        if (form.entryFeeMode === "PAID" && (form.entryFeeAmount === "" || Number(form.entryFeeAmount) <= 0)) {
            notify.error("Enter an entry fee amount greater than zero for paid categories.");
            return;
        }

        const registration: RegistrationConfig = {
            entryFeeMode: form.entryFeeMode,
            entryFeeAmount: form.entryFeeMode === "PAID" ? Number(form.entryFeeAmount) : null,
            acceptanceMode: form.acceptanceMode,
            capacity: form.capacity === "" ? null : Number(form.capacity),
            waitlistEnabled: form.waitlistEnabled,
            pendingConsumesCapacity: form.pendingConsumesCapacity,
            rosterDetailLevel: form.rosterDetailLevel,
            teamNamingMode: isTeam ? form.teamNamingMode : null,
            registrationOpenAt: localInputToIso(form.registrationOpenAt),
            registrationCloseAt: localInputToIso(form.registrationCloseAt),
            businessCoversTransactionFee: form.businessCoversTransactionFee,
        };

        try {
            await updateMutation.mutateAsync(registration);
            notify.success("Registration settings saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save registration settings"));
        }
    };

    const inputCls =
        "mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none";

    if (isLoading) {
        return <div className="py-6 text-sm text-gray-500">Loading registration…</div>;
    }

    return (
        <div className="space-y-6">
            {/* Entry fee */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-semibold text-gray-900">Entry fee</div>
                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-900">Fee mode *</label>
                        <select
                            value={form.entryFeeMode}
                            onChange={(e) => setForm((p) => ({...p, entryFeeMode: e.target.value as EntryFeeMode}))}
                            className={inputCls}
                        >
                            <option value="FREE">Free entry</option>
                            <option value="PAID">Paid entry</option>
                        </select>
                    </div>

                    {form.entryFeeMode === "PAID" ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-900">
                                Entry fee amount ({currency ?? ""}) *
                            </label>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={form.entryFeeAmount}
                                onChange={(e) => setForm((p) => ({...p, entryFeeAmount: e.target.value}))}
                                className={inputCls}
                            />
                        </div>
                    ) : null}
                </div>

                {form.entryFeeMode === "PAID" ? (
                    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">Payment fee handling</div>
                                <p className="mt-1 text-sm text-gray-600">
                                    Choose whether you <span className="font-medium">absorb</span> the payment
                                    processing fee or <span className="font-medium">pass it through</span> to the
                                    competitor.
                                </p>
                            </div>

                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                <button
                                    type="button"
                                    onClick={() => setForm((p) => ({...p, businessCoversTransactionFee: true}))}
                                    className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 transition ${
                                        form.businessCoversTransactionFee
                                            ? "bg-gray-900 text-white ring-gray-900"
                                            : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                                    }`}
                                >
                                    Absorb fees
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setForm((p) => ({...p, businessCoversTransactionFee: false}))}
                                    className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 transition ${
                                        !form.businessCoversTransactionFee
                                            ? "bg-orange-700 text-white ring-orange-600"
                                            : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50"
                                    }`}
                                >
                                    Pass-through fees
                                </button>
                            </div>
                        </div>

                        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
                            {form.businessCoversTransactionFee ? (
                                <>You <span className="font-semibold">absorb</span> the booking fee — competitors
                                    pay exactly the entry fee shown.</>
                            ) : (
                                <>The booking fee is <span className="font-semibold">added</span> to the
                                    competitor's entry fee at checkout.</>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Capacity & acceptance */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-semibold text-gray-900">Capacity & acceptance</div>
                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-900">Acceptance mode *</label>
                        <p className="mt-1 text-xs text-gray-500">How are registrations accepted?</p>
                        <select
                            value={form.acceptanceMode}
                            onChange={(e) => setForm((p) => ({...p, acceptanceMode: e.target.value as AcceptanceMode}))}
                            className={inputCls}
                        >
                            <option value="AUTO_ACCEPT">Auto accept</option>
                            <option value="MANUAL_APPROVAL">Manual approval</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Capacity</label>
                        <p className="mt-1 text-xs text-gray-500">Maximum competitors. Leave blank for unlimited.</p>
                        <input
                            type="number"
                            min={0}
                            step={1}
                            placeholder="Unlimited"
                            value={form.capacity}
                            onChange={(e) => setForm((p) => ({...p, capacity: e.target.value}))}
                            className={inputCls}
                        />
                    </div>
                </div>

                <label
                    className="mt-4 flex items-start justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">Enable waitlist</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                            When capacity is reached, additional registrations join a waitlist you can promote later.
                        </div>
                    </div>
                    <input
                        type="checkbox"
                        checked={form.waitlistEnabled}
                        onChange={(e) => setForm((p) => ({...p, waitlistEnabled: e.target.checked}))}
                        className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                    />
                </label>

                <label
                    className="mt-4 flex items-start justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">Hold slots for pending registrations</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                            When enabled, unconfirmed registrations (pending or awaiting payment) hold a capacity
                            slot until they pay, are rejected or withdraw. When disabled, only accepted competitors
                            count against capacity.
                        </div>
                    </div>
                    <input
                        type="checkbox"
                        checked={form.pendingConsumesCapacity}
                        onChange={(e) => setForm((p) => ({...p, pendingConsumesCapacity: e.target.checked}))}
                        className="mt-1 size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                    />
                </label>
            </div>

            {/* Roster detail (teams) */}
            {isTeam ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="text-sm font-semibold text-gray-900">Roster detail</div>
                    <p className="mt-1 text-xs text-gray-500">How much detail do teams provide about their roster?</p>
                    <select
                        value={form.rosterDetailLevel}
                        onChange={(e) => setForm((p) => ({
                            ...p,
                            rosterDetailLevel: e.target.value as RosterDetailLevel
                        }))}
                        className={inputCls}
                    >
                        {(Object.keys(ROSTER_LABELS) as RosterDetailLevel[]).map((k) => (
                            <option key={k} value={k}>{ROSTER_LABELS[k]}</option>
                        ))}
                    </select>

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-900">Team name</label>
                        <p className="mt-1 text-xs text-gray-500">
                            Collect a team name, or auto-derive the display name from the players' names.
                        </p>
                        <select
                            value={form.teamNamingMode}
                            onChange={(e) => setForm((p) => ({...p, teamNamingMode: e.target.value as TeamNamingMode}))}
                            className={inputCls}
                        >
                            {(Object.keys(TEAM_NAMING_LABELS) as TeamNamingMode[]).map((k) => (
                                <option key={k} value={k}>{TEAM_NAMING_LABELS[k]}</option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : null}

            {/* Registration window */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-semibold text-gray-900">Registration window</div>
                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-900">Opens at</label>
                        <input
                            type="datetime-local"
                            value={form.registrationOpenAt}
                            onChange={(e) => setForm((p) => ({...p, registrationOpenAt: e.target.value}))}
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Closes at</label>
                        <input
                            type="datetime-local"
                            value={form.registrationCloseAt}
                            onChange={(e) => setForm((p) => ({...p, registrationCloseAt: e.target.value}))}
                            className={`${inputCls} ${dateError ? "border-red-400 focus:border-red-500" : ""}`}
                        />
                        {dateError ? <p className="mt-1 text-xs text-red-600">{dateError}</p> : null}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end">
                <Button type="button" variant="primary" size="md" onClick={save} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? "Saving…" :
                        <span className="inline-flex items-center gap-2"><SaveIcon className="size-4"/>Save registration</span>}
                </Button>
            </div>
        </div>
    );
}
